import { BehaviorSubject, Observable, Subscription, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Injectable, Optional, SkipSelf, OnDestroy } from '@angular/core';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import { TimeHlp } from 'src/lib/fliko/helpers/time.helper';
import { DefectSettingsData } from 'src/lib/fliko/models/defects/defect-settings.data';
import { EstateDetailsData } from 'src/lib/fliko/models/estate/estate-detail.data';
import { EstateData } from 'src/lib/fliko/models/estate/estate.data';
import { EstateSubcontractorData, SubcontractorData } from 'src/lib/fliko/models/estate/subcontractor.data';
import { CreateServiceUser, UserCreationState } from 'src/lib/fliko/models/users/createdUser.data';
import { ServiceUserData, UserData } from 'src/lib/fliko/models/users/user.data';
import { idMap } from 'src/app/shared/operators/idMap.operator';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { ContractStatus } from 'src/app/view-company/service/view-models/compSubcontractor.view-model';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { IssueService } from '../../defects/service/issue.service';
import { SubcontractorsHelper } from '../helpers/subcontractors.helper';
import { EstateService } from './estate.service';
import {
  CreateServiceUserVM,
  SubcontractorContractsVM,
  SubcontractorLocalData,
  SubcontractorVM
} from './models/subcontractor.view.model';
import { SubcontractorsService } from './subcontractors.service';

interface BuildData {
  now: number;
  estate: EstateData;
  details: EstateDetailsData;
  defSettings: DefectSettingsData;
  estSubs: Map<string, EstateSubcontractorData>;
  crmUsersMap: Map<string, UserData>;
  serviceUsers: ServiceUserData[];
  invites: CreateServiceUser[];
}

@Injectable({ providedIn: 'root' })
export class SubcontractorsDataService implements OnDestroy {
  private readonly LAST_SELECTED_SUBCONTRACTORS_COUNT_OF = 5;

  private _list = new BehaviorSubject<SubcontractorVM[]>([]);
  private _map = new BehaviorSubject<Map<string, SubcontractorVM>>(new Map());

  private subscription$ = new Subscription();

  constructor(
    private subcontractorsService: SubcontractorsService,
    private issueService: IssueService,
    private estateService: EstateService,
    private ctxDS: ContextDataService,
    private localStorageSrv: LocalStorageService,
    @Optional() @SkipSelf() thisService?: SubcontractorsDataService
  ) {
    if (thisService) throw new Error('Multiple instances of SubcontractorsDataService');

    this.subscription$.add(
      combineLatest([
        this.ctxDS.currentEstate$.pipe(
          switchMap((estate) =>
            estate
              ? combineLatest([
                  of(estate),
                  this.subcontractorsService.getCreateServiceUsers$(estate.id),
                  this.subcontractorsService.getSubcontractorsInEstate$()
                ])
              : of([])
          )
        ),
        this.subcontractorsService.getEstateSubcontractors$().pipe(idMap()),
        this.estateService.getDetails$(),
        this.subcontractorsService.getServiceUsers$(),
        this.issueService.getDefectSettings$(),
        this.ctxDS.usersInEstate$.pipe(idMap())
      ]).subscribe(([[estate, invites, subs], estSubs, details, serviceUsers, defSettings, crmUsersMap]) => {
        if (estate && details) {
          const buildData = {
            estSubs,
            serviceUsers,
            invites,
            estate,
            details,
            defSettings,
            crmUsersMap,
            now: Date.now()
          };
          const vms = subs.map((x) => this.buildVM(x, buildData));
          this._list.next(vms);
          this._map.next(MapHlp.idMap(vms));
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public getSubcontractor$(id: string): Observable<SubcontractorVM | undefined> {
    return this._map.pipe(map((x) => x.get(id)));
  }

  public getSubcontractorsList$(): Observable<SubcontractorVM[]> {
    return this._list.asObservable();
  }

  public getActiveSubcontractorsList$(): Observable<SubcontractorVM[]> {
    return this._list.pipe(map((x) => x.filter((sub) => sub.local.isArchived != true)));
  }

  public getSubcontractorsMap$(): Observable<Map<string, SubcontractorVM>> {
    return this._map.asObservable();
  }

  public getLastSelectedSubcontractorsList(estateId: string): SubcontractorData[] {
    const subcontractorsJson = this.localStorageSrv.lastSelectedSubs.get();

    if (subcontractorsJson) {
      return JSON.parse(subcontractorsJson).filter((x: any) => {
        const subcontractor = x._localData ?? x; // HACK - handling vms accidentally saved to storage
        return subcontractor.estates.includes(estateId);
      });
    } else return [];
  }

  public addLastSelectedSubcontractor(estateId: string, subcontractor: SubcontractorData): void {
    const subcontractors = this.getLastSelectedSubcontractorsList(estateId);

    if (subcontractors.find((result) => result.id === subcontractor.id)) {
      return;
    }
    if (subcontractors.length >= this.LAST_SELECTED_SUBCONTRACTORS_COUNT_OF) {
      subcontractors.pop();
      subcontractors.unshift(subcontractor);
    } else {
      subcontractors.push(subcontractor);
    }
    this.localStorageSrv.lastSelectedSubs.set(JSON.stringify(subcontractors));
  }

  private buildVM(sub: SubcontractorData, data: BuildData): SubcontractorVM {
    const id = sub.id;
    const estSub = data.estSubs.get(id);
    const local = this.buildLocalData(sub, estSub);

    const serviceUsers = data.serviceUsers.filter((x) => x.subcontractorId === id);
    const coordinators = serviceUsers.filter((x) => !local.coordinators?.includes(x.id));
    const serviceUserMap = MapHlp.idMap(data.serviceUsers);

    const serviceInvites: CreateServiceUserVM[] = [];
    data.invites.forEach((invite) => {
      if (
        invite.subcontractorId === id &&
        invite.status !== UserCreationState.DELETED &&
        invite.estateIds.includes(data.estate.id)
      ) {
        const creator = data.crmUsersMap.get(invite.creatorId) ?? serviceUserMap.get(invite.creatorId) ?? null;
        serviceInvites.push({
          ...invite,
          creator,
          statusComment: `sub.creationComment.${invite.statusComment ?? 'invalid'}`,
          createDate: invite.createDate ?? 0
        });
      }
    });

    const isDeveloper = !!data.details?.developerId && data.details?.developerId === id;
    const contracts: SubcontractorContractsVM = { active: [], future: [], archive: [] };

    local.contracts?.forEach((x) => {
      if (TimeHlp.at(x.startDate, 0) > data.now) {
        contracts.future.push(x);
      } else if (x.indef) {
        contracts.active.push(x);
      } else {
        const contractVM = { ...x, countdown: TimeHlp.getCountdown(x.endDate) };
        if (contractVM.countdown >= 0) contracts.active.push(contractVM);
        else contracts.archive.push(contractVM);
      }
    });

    for (const key in contracts) {
      contracts[key as keyof SubcontractorContractsVM].sort(SubcontractorsHelper.contractDateCmp);
    }

    const contract = {
      status: contracts.active.length
        ? ContractStatus.ACTIVE
        : contracts.archive.length
          ? ContractStatus.ENDED
          : ContractStatus.INACTIVE,
      countdown: contracts.active[0]?.countdown ?? -1
    };

    return {
      id,
      estate: data.estate,
      global: sub,
      local,
      serviceUsers,
      coordinators,
      serviceInvites,
      defectSettings: data.defSettings,
      contracts,
      contract,
      isDeveloper,
      currency: data.details.currency
    };
  }

  // TODO: make this less bruteforce
  private buildLocalData(sub: SubcontractorData, est?: EstateSubcontractorData): SubcontractorLocalData {
    const address = sub?.address;
    const companyData = est?.companyData;
    const email = companyData?.email ?? sub.email;
    const phone = companyData?.phone ?? sub.phone;
    return {
      id: sub.id,
      estates: sub.estates,
      name: companyData?.name ?? sub.name,
      address: {
        city: companyData?.city ?? address.city,
        zipCode: companyData?.zipCode ?? address.zipCode,
        street: companyData?.street ?? address.street,
        number: companyData?.number ?? address.number,
        country: companyData?.country ?? address.country
      },
      NIP: sub.NIP,
      REGON: companyData?.REGON ?? sub.REGON,
      bankAccount: companyData?.bankAccount ?? sub.bankAccount,
      nationalRegisterOfCourt: companyData?.nationalRegisterOfCourt ?? sub.nationalRegisterOfCourt,
      email,
      phone,

      mainContact: {
        email: est?.mainContact?.email ?? email,
        phone: est?.mainContact?.phone ?? phone
      },
      types: est?.types,
      additionalContacts: est?.additionalContacts,

      contracts: est?.contracts,
      periodOfNotice: est?.periodOfNotice,

      attachments: est?.attachments,
      coordinators: est?.coordinators,

      isArchived: est?.isArchived
    };
  }
}
